/** @format */

import { styled } from "@mui/material/styles";
import { Box, Typography, IconButton } from "@mui/material";
import { colors } from "../../utility/types/constant";

export const VisualizationContainer = styled(Box)(({ theme }) => ({
	padding: "100px 20px",
	backgroundColor: "#f8f9fa",
	position: "relative",
	overflow: "hidden",
	[theme.breakpoints.down("md")]: {
		padding: "60px 20px",
	},
}));

export const SectionTitle = styled(Typography)(({ theme }) => ({
	fontFamily: "'Montserrat', sans-serif",
	fontSize: "clamp(2rem, 4vw, 3rem)",
	fontWeight: 800,
	color: colors.primary,
	marginBottom: "16px",
	letterSpacing: "-0.02em",
}));

export const SectionSubtitle = styled(Typography)(({ theme }) => ({
	fontFamily: "'Montserrat', sans-serif",
	fontSize: "clamp(1rem, 2vw, 1.25rem)",
	fontWeight: 400,
	color: "#6c757d",
	maxWidth: "700px",
	margin: "0 auto",
	lineHeight: 1.7,
}));

export const CircleDemo = styled(Box)(({ theme }) => ({
	position: "relative",
	width: "400px",
	height: "480px",
	margin: "0 auto",
	borderRadius: "15%",
	backgroundColor: "#ffffff",
	boxShadow: "0 20px 60px rgba(0,0,0,0.1)",
	border: "2px solid rgba(30, 193, 149, 0.2)",
	[theme.breakpoints.down("sm")]: {
		width: "320px",
		height: "400px",
	},
}));

export const CenterPot = styled(Box)(({ theme }) => ({
	position: "absolute",
	top: "50%",
	left: "50%",
	transform: "translate(-50%, -50%)",
	width: "140px",
	height: "140px",
	borderRadius: "50%",
	background: `linear-gradient(135deg, ${colors.mainSecondary} 0%, ${colors.headerColor} 100%)`,
	display: "flex",
	flexDirection: "column",
	alignItems: "center",
	justifyContent: "center",
	color: "#ffffff",
	boxShadow: "0 12px 32px rgba(30, 193, 149, 0.4)",
	animation: "glow 2s ease-in-out infinite",
	"@keyframes glow": {
		"0%, 100%": {
			boxShadow: "0 12px 32px rgba(30, 193, 149, 0.4)",
		},
		"50%": {
			boxShadow: "0 12px 40px rgba(30, 193, 149, 0.6)",
		},
	},
	[theme.breakpoints.down("sm")]: {
		width: "100px",
		height: "100px",
	},
}));

export const PotAmount = styled(Typography)(({ theme }) => ({
	fontFamily: "'Montserrat', sans-serif",
	fontSize: "clamp(1.5rem, 3vw, 2rem)",
	fontWeight: 800,
	marginBottom: "4px",
}));

interface MemberDotProps {
	isActive: boolean;
	hasReceived: boolean;
}

export const MemberDot = styled(Box, {
	shouldForwardProp: (prop) => prop !== "isActive" && prop !== "hasReceived",
})<MemberDotProps>(({ theme, isActive, hasReceived }) => ({
	position: "absolute",
	width: isActive ? "60px" : "50px",
	height: isActive ? "60px" : "50px",
	borderRadius: "50%",
	backgroundColor: isActive
		? colors.mainSecondary
		: hasReceived
		? colors.primary
		: "#cbd6e4",
	transform: "translate(-50%, -50%)",
	display: "flex",
	flexDirection: "column",
	alignItems: "center",
	justifyContent: "center",
	color: isActive || hasReceived ? "#ffffff" : colors.primary,
	fontWeight: 700,
	fontSize: "0.875rem",
	transition: "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
	boxShadow: isActive
		? "0 8px 24px rgba(30, 193, 149, 0.5)"
		: hasReceived
		? "0 4px 12px rgba(4, 13, 69, 0.3)"
		: "0 2px 8px rgba(0,0,0,0.1)",
	animation: isActive ? "pulse 1.5s ease-in-out infinite" : "none",
	"@keyframes pulse": {
		"0%, 100%": {
			transform: "translate(-50%, -50%) scale(1)",
		},
		"50%": {
			transform: "translate(-50%, -50%) scale(1.1)",
		},
	},
	[theme.breakpoints.down("sm")]: {
		width: isActive ? "45px" : "40px",
		height: isActive ? "45px" : "40px",
		fontSize: "0.75rem",
	},
}));

export const ControlButton = styled(IconButton)(({ theme }) => ({
	position: "absolute",
	bottom: "0px",
	left: "50%",
	transform: "translateX(-50%)",
	backgroundColor: colors.mainSecondary,
	color: "#ffffff",
	width: "56px",
	height: "56px",
	boxShadow: "0 8px 20px rgba(30, 193, 149, 0.4)",
	"&:hover": {
		backgroundColor: colors.headerColor,
	},
	[theme.breakpoints.down("sm")]: {
		bottom: "10px",
	},
}));

export const Legend = styled(Box)(({ theme }) => ({
	marginTop: "40px",
	display: "flex",
	flexDirection: "column",
	gap: "16px",
}));

export const LegendItem = styled(Box)(({ theme }) => ({
	display: "flex",
	alignItems: "center",
	gap: "12px",
}));

interface LegendDotProps {
	status: "active" | "received" | "waiting";
}

export const LegendDot = styled(Box, {
	shouldForwardProp: (prop) => prop !== "status",
})<LegendDotProps>(({ theme, status }) => ({
	width: "20px",
	height: "20px",
	borderRadius: "50%",
	backgroundColor:
		status === "active"
			? colors.mainSecondary
			: status === "received"
			? colors.primary
			: "#cbd6e4",
}));

export const LegendText = styled(Typography)(({ theme }) => ({
	fontFamily: "'Montserrat', sans-serif",
	fontSize: "1rem",
	fontWeight: 500,
	color: "#4a5568",
}));

export const InfoPanel = styled(Box)(({ theme }) => ({
	backgroundColor: "#ffffff",
	borderRadius: "16px",
	padding: "30px",
	boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
	border: "2px solid rgba(30, 193, 149, 0.2)",
	marginBottom: "20px",
}));

export const InfoTitle = styled(Typography)(({ theme }) => ({
	fontFamily: "'Montserrat', sans-serif",
	fontSize: "1.25rem",
	fontWeight: 700,
	color: colors.primary,
	marginBottom: "12px",
}));

export const InfoText = styled(Typography)(({ theme }) => ({
	fontFamily: "'Montserrat', sans-serif",
	fontSize: "1rem",
	fontWeight: 400,
	color: "#4a5568",
	lineHeight: 1.7,
}));
